import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { supabase } from "../lib/supabase";

/**
 * ClientContext - Platform-wide client selection and management
 * 
 * This context provides:
 * - Current selected client state
 * - Client selection methods
 * - Client data fetching and caching
 * - Integration with Assignment Center and other lots
 * - Persistence across page reloads
 */


const ClientContext = createContext(null);


export const ClientProvider = ({ children }) => {
  const [selectedClient, setSelectedClient] = useState(null);
  const [clients, setClients] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [isCALMGarage, setIsCALMGarage] = useState(false);

  // Fetch client by ID - moved before useEffect that uses it
  const fetchClientById = useCallback(async (clientId) => {
    if (!clientId) return null;
    
    setIsLoading(true);
    setError(null);
    
    try {
      const { data: clientDoc, error } = await supabase
        .from('clients')
        .select('*')
        .eq('id', clientId)
        .single();
      if (clientDoc && !error) {
        const clientData = { id: clientDoc.id, ...clientDoc };
        setSelectedClient(clientData);
        
        // Save to localStorage
        localStorage.setItem('odv_selected_client', JSON.stringify(clientData));
        localStorage.setItem('odv_selected_client_id', clientId);
        
        return clientData;
      } else {
        throw new Error('Client not found');
      }
    } catch (err) {
      console.error('Error fetching client:', err);
      setError(err.message);
      return null;
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Load selected client from localStorage on mount
  useEffect(() => {
    try {
      const savedClientId = localStorage.getItem('odv_selected_client_id');
      const savedClient = localStorage.getItem('odv_selected_client');
      
      if (savedClient) {
        setSelectedClient(JSON.parse(savedClient));
      } else if (savedClientId) {
        // If we only have ID, fetch the full client data
        fetchClientById(savedClientId);
      }
    } catch (err) {
      console.error('Error loading saved client:', err);
      // Clear corrupted data
      localStorage.removeItem('odv_selected_client');
      localStorage.removeItem('odv_selected_client_id');
    }
  }, [fetchClientById]);

  // Detect if we're in a CALM garage context
  useEffect(() => {
    const currentPath = window.location.pathname;
    setIsCALMGarage(currentPath.includes('/calm') || currentPath.includes('/hr'));
  }, []);

  // Fetch all clients (for ClientSelector dropdown)
  
const fetchClients = useCallback(async (filters = {}) => {
    setIsLoading(true);
    setError(null);
    
    try {
      let clientQuery = supabase
        .from('clients')
        .select('*');
      
      // Apply filters if provided
      
if (filters.type) {
        clientQuery = clientQuery.eq('type', filters.type);
      }

if (filters.active !== undefined) {
        clientQuery = clientQuery.eq('active', filters.active);
      }
      
      const { data: clientList, error } = await clientQuery;
      
      if (error) throw error;
      
      setClients(clientList || []);
      return clientList || [];
    } catch (err) {
      console.error('Error fetching clients:', err);
      setError(err.message);
      return [];
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Select a client (used by ClientSelector)
  
const selectClient = useCallback((client) => {
    
if (!client) {
      setSelectedClient(null);
      localStorage.removeItem('odv_selected_client');
      localStorage.removeItem('odv_selected_client_id');
      return;
    }

    setSelectedClient(client);
    localStorage.setItem('odv_selected_client', JSON.stringify(client));
    localStorage.setItem('odv_selected_client_id', client.id);
  }, []);

  // Clear client selection
  
const clearClient = useCallback(() => {
    setSelectedClient(null);
    localStorage.removeItem('odv_selected_client');
    localStorage.removeItem('odv_selected_client_id');
  }, []);

  // Get client display name
  
const getClientDisplayName = useCallback((client = selectedClient) => {
    if (!client) return 'No Client Selected';
    return client.name || client.companyName || client.displayName || `Client ${client.id}`;
  }, [selectedClient]);

  // Check if client is selected
  
const hasSelectedClient = Boolean(selectedClient);

  
const value = {
    // State
    selectedClient,
    clients,
    isLoading,
    error,
    isCALMGarage,
    hasSelectedClient,
    
    // Actions
    selectClient,
    clearClient,
    fetchClientById,
    fetchClients,
    getClientDisplayName,
    
    // Legacy compatibility for existing components
    setSelectedClient: selectClient
  };

  return (
    <ClientContext.Provider value={value}>
      {children}
    </ClientContext.Provider>
  );
};

// Hook to use client context

export const useClientContext = () => {
  const context = useContext(ClientContext);
  if (!context) {
    throw new Error('useClientContext must be used within a ClientProvider');
  }
  return context;
};

// Legacy compatibility hook for existing components

export const useClient = useClientContext;


export default ClientContext;
